/**
 * RTK Query slice for the Stripe Identity verification flow.
 *
 * Two surfaces:
 *   - `useFetchIdentityStatusQuery()`: current verification state for the
 *     signed-in user, hydrated from `GET /api/identity/status`.
 *   - `useVerifyIdentityMutation()`: drives the full sequence in one
 *     dispatch: `POST /api/identity/start` → `stripe.verifyIdentity()` →
 *     re-fetch status. Stripe never appears in components.
 *
 * `useIdentityStatus()` wraps both with a status-driven Supabase Realtime
 * subscription that only opens while Stripe is mid-review.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  fetchIdentityStatus as fetchIdentityStatusApi,
  startIdentityVerification as startIdentityVerificationApi,
  startIdentityCheckout as startIdentityCheckoutApi,
} from '../util/api';
import * as log from '../util/log';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// ── Domain types ────────────────────────────────────────────────────────────

/** Stripe Identity verification session statuses (vendor strings). */
export const IDENTITY_STATUS = {
  RequiresInput: 'requires_input',
  Processing: 'processing',
  Verified: 'verified',
  Canceled: 'canceled',
} as const;
export type IdentityStatus = (typeof IDENTITY_STATUS)[keyof typeof IDENTITY_STATUS];

/** `is_identity_verified` int flag. Stripe only ever flips 0 → 1. */
export const IDENTITY_FLAG = { Unverified: 0, Verified: 1 } as const;

/** Stripe.js error codes from `stripe.verifyIdentity()`. */
const STRIPE_SESSION_CANCELLED = 'session_cancelled';

/** Stable error codes the FE branches on / shows translated messages for. */
export const IDENTITY_ERROR = {
  StatusFailed: 'IDENTITY_STATUS_FAILED',
  VerifyFailed: 'IDENTITY_VERIFY_FAILED',
  VerifyUnavailable: 'IDENTITY_VERIFY_UNAVAILABLE',
  CheckoutFailed: 'IDENTITY_CHECKOUT_FAILED',
} as const;
export type IdentityError = (typeof IDENTITY_ERROR)[keyof typeof IDENTITY_ERROR];

/** Args for `verifyIdentity`. When `sessionId` is set the server treats this as the paid path. */
export type VerifyIdentityArgs = { sessionId?: string };

/** Wire shape of `POST /api/identity/checkout`. */
export type IdentityCheckoutResponse = {
  clientSecret: string;
  sessionId: string;
  amountCents: number;
  currency: string;
};

/** Outcomes of the verify mutation. */
export const VERIFY_OUTCOME = { Launched: 'launched', Cancelled: 'cancelled' } as const;
export type VerifyOutcome = (typeof VERIFY_OUTCOME)[keyof typeof VERIFY_OUTCOME];

/** Wire shape of `GET /api/identity/status`. */
export type IdentityStatusResponse = {
  lastStatus: IdentityStatus | null;
  isVerified: boolean;
  verifiedAt: string | null;
  lastSessionId: string | null;
  updatedAt: string | null;
  /** True when the user has an unconsumed succeeded paid-identity fee on file. */
  hasPaidVerificationCredit: boolean;
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** True when the user has passed Stripe verification. */
export const isVerified = (s: IdentityStatusResponse | undefined): boolean =>
  !!s && (s.lastStatus === IDENTITY_STATUS.Verified || s.isVerified);

/** True when Stripe is mid-review (webhook in flight). */
export const isProcessing = (s: IdentityStatusResponse | undefined): boolean =>
  s?.lastStatus === IDENTITY_STATUS.Processing;

/** True when the user needs another attempt (rejected or bailed). */
export const needsRetry = (s: IdentityStatusResponse | undefined): boolean =>
  s?.lastStatus === IDENTITY_STATUS.RequiresInput ||
  s?.lastStatus === IDENTITY_STATUS.Canceled;

/** True when the user has never started a verification session. */
export const neverStarted = (s: IdentityStatusResponse | undefined): boolean =>
  !!s && !s.lastStatus;

const toRtkError = (e: unknown, code: IdentityError) => {
  log.error(e instanceof Error ? e : new Error(String(e)), 'identity.api.failed', { code });
  return { error: { status: (e as { status?: number })?.status || 500, data: { code } } };
};

const launchStripeModal = async (clientSecret: string): Promise<VerifyOutcome> => {
  const stripe = (window as any).Stripe?.(STRIPE_PUBLISHABLE_KEY);
  if (!stripe) throw new Error('Stripe.js missing or key not configured');
  const { error } = await stripe.verifyIdentity(clientSecret);
  if (!error) return VERIFY_OUTCOME.Launched;
  if (error.code === STRIPE_SESSION_CANCELLED) return VERIFY_OUTCOME.Cancelled;
  throw new Error(error.message || 'stripe.verifyIdentity failed');
};

// ── Slice ───────────────────────────────────────────────────────────────────

export const identityApi = createApi({
  reducerPath: 'identityApi',
  baseQuery: () => ({ data: null }),
  tagTypes: ['IdentityStatus'],
  endpoints: build => ({
    fetchIdentityStatus: build.query<IdentityStatusResponse, void>({
      providesTags: ['IdentityStatus'],
      async queryFn() {
        try {
          const data = (await fetchIdentityStatusApi()) as IdentityStatusResponse;
          return { data };
        } catch (e) {
          return toRtkError(e, IDENTITY_ERROR.StatusFailed);
        }
      },
    }),
    verifyIdentity: build.mutation<{ outcome: VerifyOutcome }, VerifyIdentityArgs | void>({
      // Intentionally NOT invalidating ['IdentityStatus'] — that would
      // refetch immediately after the modal closes, before Stripe's
      // verified webhook lands, and overwrite the optimistic `processing`
      // value below. Supabase Realtime (opened while isProcessing) handles
      // the post-webhook refetch.
      async queryFn(args, api) {
        try {
          if (!STRIPE_PUBLISHABLE_KEY) {
            return toRtkError(new Error('STRIPE_PUBLISHABLE_KEY missing'), IDENTITY_ERROR.VerifyUnavailable);
          }
          const body = args?.sessionId ? { sessionId: args.sessionId } : {};
          const { clientSecret } = (await startIdentityVerificationApi(body)) as { clientSecret: string };
          const outcome = await launchStripeModal(clientSecret);
          if (outcome === VERIFY_OUTCOME.Launched) {
            // Optimistically flip the cache to `processing` so the card shows
            // "Verification in progress" the instant the Stripe modal closes.
            // The webhook lands seconds-to-minutes later; the Realtime
            // subscription (opened while isProcessing) then refetches and
            // flips to `verified`.
            api.dispatch(
              identityApi.util.updateQueryData('fetchIdentityStatus', undefined, draft => {
                if (!draft) return;
                draft.lastStatus = IDENTITY_STATUS.Processing;
              })
            );
          }
          return { data: { outcome } };
        } catch (e) {
          return toRtkError(e, IDENTITY_ERROR.VerifyFailed);
        }
      },
    }),
    startIdentityCheckout: build.mutation<IdentityCheckoutResponse, void>({
      async queryFn() {
        try {
          const data = (await startIdentityCheckoutApi()) as IdentityCheckoutResponse;
          return { data };
        } catch (e) {
          return toRtkError(e, IDENTITY_ERROR.CheckoutFailed);
        }
      },
    }),
    // Create the Stripe Identity session on the server (retrieve-and-check the
    // paid Checkout Session) WITHOUT opening the modal. Establishes the paid +
    // started state; the modal is launched separately via launchVerification.
    createIdentityVerification: build.mutation<{ clientSecret: string }, VerifyIdentityArgs | void>({
      async queryFn(args) {
        try {
          const body = args?.sessionId ? { sessionId: args.sessionId } : {};
          const { clientSecret } = (await startIdentityVerificationApi(body)) as {
            clientSecret: string;
          };
          return { data: { clientSecret } };
        } catch (e) {
          return toRtkError(e, IDENTITY_ERROR.VerifyFailed);
        }
      },
    }),
    // Post-payment: ask the BFF to confirm this Checkout Session against Stripe
    // and write the returned real status into the status query cache.
    confirmIdentityPayment: build.mutation<IdentityStatusResponse, string>({
      async queryFn(sessionId, api) {
        try {
          const data = (await fetchIdentityStatusApi(sessionId)) as IdentityStatusResponse;
          api.dispatch(
            identityApi.util.updateQueryData('fetchIdentityStatus', undefined, () => data)
          );
          return { data };
        } catch (e) {
          return toRtkError(e, IDENTITY_ERROR.StatusFailed);
        }
      },
    }),
  }),
});

export const {
  useFetchIdentityStatusQuery,
  useVerifyIdentityMutation,
  useStartIdentityCheckoutMutation,
  useCreateIdentityVerificationMutation,
  useConfirmIdentityPaymentMutation,
} = identityApi;
export default identityApi.reducer;

const IDENTITY_POLL_MS = 1500;
const IDENTITY_POLL_LIMIT_MS = 2 * 60 * 1000;

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * Identity verification state + actions. While a verification is
 * `processing`, polls the status endpoint so the UI flips once Stripe
 * finishes. Realtime is not used: the table is backend-only, absent from the
 * realtime publication, and carries no RLS to scope a browser subscription.
 */
export const useIdentityStatus = () => {
  const dispatch = useDispatch<any>();
  const query = useFetchIdentityStatusQuery();
  const [verify, verifyState] = useVerifyIdentityMutation();
  const [startCheckout, checkoutState] = useStartIdentityCheckoutMutation();
  const [createVerify, createVerifyState] = useCreateIdentityVerificationMutation();
  const [confirmPayment] = useConfirmIdentityPaymentMutation();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Polled, not Realtime. identity_status lives in the internal schema, which
  // is deliberately not in the realtime publication and has RLS disabled, so a
  // browser subscription would either match nothing or leak other users' rows.
  useEffect(() => {
    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    if (!isProcessing(query.data)) {
      stop();
      return undefined;
    }

    if (!pollRef.current) {
      let elapsed = 0;
      pollRef.current = setInterval(() => {
        elapsed += IDENTITY_POLL_MS;
        dispatch(identityApi.util.invalidateTags(['IdentityStatus']));
        // Stripe usually lands in seconds; give up rather than poll forever.
        if (elapsed >= IDENTITY_POLL_LIMIT_MS) stop();
      }, IDENTITY_POLL_MS);
    }

    return stop;
  }, [query.data, dispatch]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    hasFetchError: query.isError,
    fetchError: query.error as { data?: { code?: IdentityError } } | undefined,
    refetch: query.refetch,
    isVerified: isVerified(query.data),
    isProcessing: isProcessing(query.data),
    needsRetry: needsRetry(query.data),
    neverStarted: neverStarted(query.data),
    hasPaidVerificationCredit: !!query.data?.hasPaidVerificationCredit,
    isVerifying: verifyState.isLoading,
    hasVerifyError: verifyState.isError,
    verifyError: verifyState.error as { data?: { code?: IdentityError } } | undefined,
    verify: (args?: VerifyIdentityArgs) => {
      void verify(args);
    },
    isStartingCheckout: checkoutState.isLoading,
    hasCheckoutError: checkoutState.isError,
    startCheckout: async (): Promise<IdentityCheckoutResponse | null> => {
      const result = await startCheckout();
      if ('data' in result && result.data) return result.data;
      return null;
    },
    // Confirm a paid Checkout Session with the BFF; resolves once the real
    // status is in Redux.
    confirmPayment: (sessionId: string) => confirmPayment(sessionId).unwrap(),
    // Create the verification session now (server retrieve-and-check), without
    // launching. Returns the clientSecret for a later launch, or null on error.
    isCreatingVerification: createVerifyState.isLoading,
    createVerification: async (args?: VerifyIdentityArgs): Promise<string | null> => {
      const result = await createVerify(args);
      if ('data' in result && result.data) {
        // Sync the card behind the modal: the fee is paid and a verification
        // session now exists, so reflect the started state immediately instead
        // of leaving it on the paywall until the webhook-written row lands.
        dispatch(
          identityApi.util.updateQueryData('fetchIdentityStatus', undefined, draft => {
            if (draft && !draft.lastStatus) draft.lastStatus = IDENTITY_STATUS.RequiresInput;
          })
        );
        return result.data.clientSecret;
      }
      return null;
    },
    launchVerification: async (clientSecret: string): Promise<void> => {
      const outcome = await launchStripeModal(clientSecret);
      if (outcome === VERIFY_OUTCOME.Launched) {
        dispatch(
          identityApi.util.updateQueryData('fetchIdentityStatus', undefined, draft => {
            if (draft) draft.lastStatus = IDENTITY_STATUS.Processing;
          })
        );
      }
    },
  };
};
